import api from './api'
import { AxiosResponse } from 'axios'

// Types pour les notifications
export type NotificationType = 'info' | 'warning' | 'error' | 'success'

// Interfaces pour les notifications
export interface Notification {
  _id: string
  title: string
  message: string
  type: NotificationType
  category?: string
  read: boolean
  userId?: string
  organizationId?: string
  metadata?: {
    [key: string]: any
  }
  createdAt: string
  updatedAt: string
  readAt?: string
}

export interface CreateNotificationData {
  title: string
  message: string
  type: NotificationType
  category?: string
  userId?: string
  organizationId?: string
  metadata?: {
    [key: string]: any
  }
}

export interface UpdateNotificationData {
  title?: string
  message?: string
  type?: NotificationType
  category?: string
  read?: boolean
  metadata?: {
    [key: string]: any
  }
}

export interface NotificationFilters {
  type?: string
  category?: string
  read?: boolean
  userId?: string
  organizationId?: string
  search?: string
  dateFrom?: string
  dateTo?: string
  page?: number
  limit?: number
}

export interface NotificationStats {
  total: number
  unread: number
  read: number
  today: number
  byType: {
    info: number
    warning: number
    error: number
    success: number
  }
  byCategory: Record<string, number>
}

class NotificationService {
  // Récupérer toutes les notifications avec filtres
  async getNotifications(filters: NotificationFilters = {}): Promise<any> {
    try {
      const response = await api.get('/notifications', {
        params: filters
      })
      return response.data.data
    } catch (error) {
      console.error('Erreur lors de la récupération des notifications:', error)
      throw error
    }
  }

  // Récupérer une notification par ID
  async getNotificationById(id: string): Promise<Notification> {
    try {
      const response: AxiosResponse<Notification> = await api.get(`/notifications/${id}`)
      return response.data
    } catch (error) {
      console.error('Erreur lors de la récupération de la notification:', error)
      throw error
    }
  }

  // Créer une nouvelle notification
  async createNotification(data: CreateNotificationData): Promise<Notification> {
    try {
      const response: AxiosResponse<Notification> = await api.post('/notifications', data)
      return response.data
    } catch (error) {
      console.error('Erreur lors de la création de la notification:', error)
      throw error
    }
  }

  // Mettre à jour une notification
  async updateNotification(id: string, data: UpdateNotificationData): Promise<Notification> {
    try {
      const response: AxiosResponse<Notification> = await api.patch(`/notifications/${id}`, data)
      return response.data
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la notification:', error)
      throw error
    }
  }

  // Supprimer une notification
  async deleteNotification(id: string): Promise<void> {
    try {
      await api.delete(`/notifications/${id}`)
    } catch (error) {
      console.error('Erreur lors de la suppression de la notification:', error)
      throw error
    }
  }

  // Marquer une notification comme lue
  async markAsRead(id: string): Promise<Notification> {
    try {
      const response: AxiosResponse<Notification> = await api.patch(`/notifications/${id}/read`)
      return response.data
    } catch (error) {
      console.error('Erreur lors du marquage de la notification comme lue:', error)
      throw error
    }
  }

  // Marquer toutes les notifications comme lues
  async markAllAsRead(userId?: string): Promise<void> {
    try {
      await api.patch('/notifications/mark-all-read', { userId })
    } catch (error) {
      console.error('Erreur lors du marquage de toutes les notifications comme lues:', error)
      throw error
    }
  }

  // Supprimer toutes les notifications
  async deleteAllNotifications(userId?: string): Promise<void> {
    try {
      await api.delete('/notifications/delete-all', { 
        params: { userId } 
      })
    } catch (error) {
      console.error('Erreur lors de la suppression de toutes les notifications:', error)
      throw error
    }
  }

  // Récupérer les statistiques des notifications
  async getNotificationStats(filters: Omit<NotificationFilters, 'page' | 'limit'> = {}): Promise<NotificationStats> {
    try {
      const response: AxiosResponse<NotificationStats> = await api.get('/notifications/stats', {
        params: filters
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques des notifications:', error)
      throw error
    }
  }

  // Récupérer les notifications non lues
  async getUnreadNotifications(userId?: string): Promise<{ data: Notification[] }> {
    try {
      const response: AxiosResponse<Notification[]> = await api.get('/notifications/unread', {
        params: { userId }
      })
      // S'assurer que la réponse est toujours un objet avec une propriété data qui est un tableau
      return {
        data: Array.isArray(response.data) ? response.data : []
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des notifications non lues:', error)
      // Retourner un objet avec un tableau vide en cas d'erreur
      return { data: [] }
    }
  }

  // Créer une notification pour un utilisateur spécifique
  async createUserNotification(userId: string, data: Omit<CreateNotificationData, 'userId'>): Promise<Notification> {
    try {
      const response: AxiosResponse<Notification> = await api.post('/notifications', {
        ...data,
        userId
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de la création de la notification utilisateur:', error)
      throw error
    }
  }

  // Créer une notification pour une organisation
  async createOrganizationNotification(organizationId: string, data: Omit<CreateNotificationData, 'organizationId'>): Promise<Notification> {
    try {
      const response: AxiosResponse<Notification> = await api.post('/notifications', {
        ...data,
        organizationId
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de la création de la notification organisation:', error)
      throw error
    }
  }

  // Rechercher des notifications
  async searchNotifications(query: string, filters: Omit<NotificationFilters, 'search'> = {}): Promise<{ notifications: Notification[], total: number }> {
    try {
      const response: AxiosResponse<{ notifications: Notification[], total: number }> = await api.get('/notifications/search', {
        params: {
          ...filters,
          q: query
        }
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de la recherche des notifications:', error)
      throw error
    }
  }

  // ===== MÉTHODES D'ENVOI DE NOTIFICATIONS =====

  // Envoyer une notification par email
  async sendEmailNotification(data: {
    to: string | string[]
    subject: string
    text?: string
    html?: string
    from?: string
    fromName?: string
    provider?: string
    priority?: 'low' | 'normal' | 'high'
    category?: string
    tags?: string[]
  }): Promise<{ success: boolean; message: string; provider?: string }> {
    try {
      const response: AxiosResponse<{ success: boolean; message: string; provider?: string }> = await api.post('/notifications/send/email', data)
      return response.data
    } catch (error) {
      console.error('Erreur lors de l\'envoi d\'email:', error)
      throw error
    }
  }

  // Envoyer une notification Slack
  async sendSlackNotification(data: {
    webhookUrl?: string
    channel?: string
    username?: string
    text: string
    attachments?: any[]
  }): Promise<{ success: boolean; message: string }> {
    try {
      const response: AxiosResponse<{ success: boolean; message: string }> = await api.post('/notifications/send/slack', data)
      return response.data
    } catch (error) {
      console.error('Erreur lors de l\'envoi Slack:', error)
      throw error
    }
  }

  // Envoyer une notification Discord
  async sendDiscordNotification(data: {
    webhookUrl?: string
    username?: string
    avatarUrl?: string
    content: string
    embeds?: any[]
  }): Promise<{ success: boolean; message: string }> {
    try {
      const response: AxiosResponse<{ success: boolean; message: string }> = await api.post('/notifications/send/discord', data)
      return response.data
    } catch (error) {
      console.error('Erreur lors de l\'envoi Discord:', error)
      throw error
    }
  }

  // Envoyer une notification multi-provider
  async sendMultiProviderNotification(data: {
    to: string | string[]
    subject: string
    text?: string
    html?: string
    from?: string
    fromName?: string
  }): Promise<{ results: any[]; successCount: number; failureCount: number }> {
    try {
      const response: AxiosResponse<{ results: any[]; successCount: number; failureCount: number }> = await api.post('/notifications/send/multi', data)
      return response.data
    } catch (error) {
      console.error('Erreur lors de l\'envoi multi-provider:', error)
      throw error
    }
  }

  // ===== MÉTHODES DE TEST =====

  // Tester l'envoi d'email
  async testEmailNotification(): Promise<any> {
    try {
      const response: AxiosResponse<any> = await api.post('/notifications/test/email')
      return response.data
    } catch (error) {
      console.error('Erreur lors du test email:', error)
      throw error
    }
  }

  // Tester l'envoi Slack
  async testSlackNotification(): Promise<any> {
    try {
      const response: AxiosResponse<any> = await api.post('/notifications/test/slack')
      return response.data
    } catch (error) {
      console.error('Erreur lors du test Slack:', error)
      throw error
    }
  }

  // Tester l'envoi Discord
  async testDiscordNotification(): Promise<any> {
    try {
      const response: AxiosResponse<any> = await api.post('/notifications/test/discord')
      return response.data
    } catch (error) {
      console.error('Erreur lors du test Discord:', error)
      throw error
    }
  }

  // Tester l'envoi multi-provider
  async testMultiProviderNotification(): Promise<any> {
    try {
      const response: AxiosResponse<any> = await api.post('/notifications/test/multi')
      return response.data
    } catch (error) {
      console.error('Erreur lors du test multi-provider:', error)
      throw error
    }
  }

  // Tester tous les providers
  async testAllProviders(): Promise<any> {
    try {
      const response: AxiosResponse<any> = await api.post('/notifications/test/all')
      return response.data
    } catch (error) {
      console.error('Erreur lors du test de tous les providers:', error)
      throw error
    }
  }

  // ===== MÉTHODES DE TEST RESEND SPÉCIFIQUES =====

  // Tester la connexion Resend
  async testResendConnection(data: {
    apiKey: string
    fromEmail: string
    fromName?: string
  }): Promise<any> {
    try {
      const response: AxiosResponse<any> = await api.post('/notifications/test/resend/connection', data)
      return response.data
    } catch (error) {
      console.error('Erreur lors du test de connexion Resend:', error)
      throw error
    }
  }

  // Tester l'envoi d'email Resend
  async testResendEmail(data: {
    apiKey: string
    fromEmail: string
    toEmail: string
    fromName?: string
  }): Promise<any> {
    try {
      const response: AxiosResponse<any> = await api.post('/notifications/test/resend/email', data)
      return response.data
    } catch (error) {
      console.error('Erreur lors du test d\'envoi Resend:', error)
      throw error
    }
  }

  // Tester la validation de domaine Resend
  async testResendDomain(data: {
    apiKey: string
    domain: string
  }): Promise<any> {
    try {
      const response: AxiosResponse<any> = await api.post('/notifications/test/resend/domain', data)
      return response.data
    } catch (error) {
      console.error('Erreur lors du test de domaine Resend:', error)
      throw error
    }
  }

  // Test complet Resend
  async testResendComplete(data: {
    apiKey: string
    fromEmail: string
    toEmail: string
    fromName?: string
  }): Promise<any> {
    try {
      const response: AxiosResponse<any> = await api.post('/notifications/test/resend/complete', data)
      return response.data
    } catch (error) {
      console.error('Erreur lors du test complet Resend:', error)
      throw error
    }
  }
}

export const notificationService = new NotificationService()
